import React, { useState, useMemo, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';

import {
  TEMPLATE_ROLE_PRIORITY_MAP,
  TemplateRoles,
  TemplateUsage,
} from '@bb/domain/dist/types';
import { FroalaTaggedInput, message as antMessage, theme } from 'components';
import { replaceTagsWithMarkup, sortTemplates } from 'utils';
import { AuthContextType } from 'contexts';
import TemplateHeader from '../../../Router/pages/TemplateManager/TemplateHeader';
import { TemplateDropdownType } from '../../template/TemplateDropdown';

export interface OfferMessageEditorProps {
  templates: TemplateDropdownType;
  message?: { subject?: string; body?: string };
  signature: string;
  defaultSubjectPostfix?: string;
  hasFeature: AuthContextType['hasFeature'];
  setSubject: (value: string) => void;
  setBody: (value: string) => void;
}

const OfferMessageEditor = (props: OfferMessageEditorProps) => {
  const {
    templates,
    message,
    signature,
    defaultSubjectPostfix = '',
    hasFeature,
    setSubject,
    setBody,
  } = props;

  const [selectedTemplate, setSelectedTemplate] = useState('');
  // FIXME: Refactor Parent Component into React
  const [reactSubject, setReactSubject] = useState(message?.subject);
  const [reactBody, setReactBody] = useState(message?.body);

  const [previewing, setPreviewing] = React.useState(false);

  const processedTemplates = useMemo(() => {
    let processed = cloneDeep(templates).map(t => {
      // eslint-disable-next-line no-param-reassign
      t.subject = [t.subject, defaultSubjectPostfix]
        .filter(Boolean)
        .join(' - ');
      return t;
    });

    processed = sortTemplates(processed);
    // Add a custom template selection
    const custom = {
      body: signature,
      id: 'custom',
      name: 'Custom',
      subject: defaultSubjectPostfix,
      type: 'propertyAnnouncement' as TemplateUsage,
      role: 'USER' as TemplateRoles,
    };
    processed.push(custom);

    return processed;
  }, [templates]);

  // Init the message body
  useEffect(() => {
    const primaryDefaultId = processedTemplates
      .filter(t => t.isDefault)
      .sort(
        (a, b) =>
          TEMPLATE_ROLE_PRIORITY_MAP[a.role] -
          TEMPLATE_ROLE_PRIORITY_MAP[b.role],
      )[0]?.id;
    setSelectedTemplate(primaryDefaultId || 'custom');
  }, [processedTemplates]);

  useEffect(() => {
    const newTemplate = processedTemplates.find(t => t.id === selectedTemplate);
    setReactBody(newTemplate?.body || '');
    if (typeof newTemplate?.subject === 'string')
      setReactSubject(newTemplate.subject);
  }, [selectedTemplate, processedTemplates]);

  useEffect(() => {
    setSubject(reactSubject);
  }, [reactSubject]);

  useEffect(() => {
    setBody(reactBody);
  }, [reactBody]);

  const changeTemplates = (id: string) => {
    const newTemplate = processedTemplates.some(t => t.id === id);
    if (!newTemplate) return;

    setSelectedTemplate(id);
  };

  const onClickDisabledInput = () => {
    antMessage.warning({
      content: (
        <span>
          Click <strong>Exit Preview</strong> to make changes.
        </span>
      ),
    });
  };

  return (
    <>
      <TemplateHeader
        templates={processedTemplates}
        selectedTemplate={selectedTemplate}
        subject={selectedTemplate ? reactSubject : ''}
        previewing={previewing}
        limitWidth
        onPreview={setPreviewing}
        onDropdownChange={changeTemplates}
        onInputChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setReactSubject(e.target.value)
        }
      />
      <FroalaTaggedInputContainer>
        <FroalaTaggedInput
          model={replaceTagsWithMarkup((selectedTemplate && reactBody) || '')}
          onModelChange={setReactBody}
          templateType="propertyAnnouncement"
          preview={previewing}
          onClick={() => {
            if (previewing) onClickDisabledInput();
          }}
          hasFeature={hasFeature}
        />
      </FroalaTaggedInputContainer>
    </>
  );
};

const FroalaTaggedInputContainer = styled.div`
  & a {
    color: ${theme.blueBase};
  }
`;

export default OfferMessageEditor;
